// engine2025/money.ts — F0.T0.1 (Cents + parseEuros) y F0.T0.4 (redondeo AEAT).
//
// Regla dura §0.7 PLAN_IMPLEMENTACION_IRPF.md: dinero = céntimos enteros. Prohibido operar
// con floats en euros en cualquier bloque del motor; toda conversión euros↔céntimos pasa
// por este fichero (borde único de entrada/salida de dinero).

/** Céntimo entero. Marca de tipo para evitar mezclar euros (float) con céntimos por error. */
export type Cents = number & { readonly __brand: 'Cents' };

/** Verifica que `value` es un entero de céntimos válido. */
export function assertCents(value: number): asserts value is Cents {
    if (!Number.isInteger(value)) {
        throw new TypeError(
            `Cents debe ser un entero; recibido ${value}. Prohibido operar con floats en euros ` +
            '(regla dura §0.7 PLAN_IMPLEMENTACION_IRPF.md).'
        );
    }
}

/** Convierte un `number` ya expresado en céntimos a `Cents`, validando que sea entero. */
export function toCents(value: number): Cents {
    assertCents(value);
    return value;
}

// Formato español de importe: miles con "." y 2 decimales con ",". Ej.: "1.234,56", "-90,00".
const SPANISH_EURO_FORMAT = /^-?\d{1,3}(\.\d{3})*,\d{2}$/;

/**
 * Parsea un importe en formato español del manual AEAT ("1.234,56") a céntimos enteros.
 * Único punto de entrada de dinero declarado en los fixtures golden (los fixtures expresan
 * euros con 2 decimales; el harness convierte — §0.7 del plan).
 */
export function parseEuros(input: string): Cents {
    const trimmed = input.trim();
    if (!SPANISH_EURO_FORMAT.test(trimmed)) {
        throw new TypeError(
            `Formato de importe no reconocido: "${input}". Se espera formato español "1.234,56" (2 decimales).`
        );
    }
    const negative = trimmed.startsWith('-');
    const digitsOnly = (negative ? trimmed.slice(1) : trimmed).replace(/\./g, '').replace(',', '');
    const cents = Number(digitsOnly) * (negative ? -1 : 1);
    return toCents(cents);
}

/** Suma dos importes en céntimos. */
export function addCents(a: Cents, b: Cents): Cents {
    return toCents(a + b);
}

/** Resta dos importes en céntimos. Puede dar negativo si el campo es `signed` (el caller decide). */
export function subtractCents(a: Cents, b: Cents): Cents {
    return toCents(a - b);
}

/** Suma una lista de importes en céntimos. */
export function sumCents(values: Cents[]): Cents {
    return toCents(values.reduce((acc, v) => acc + v, 0));
}

export function minCents(a: Cents, b: Cents): Cents {
    return toCents(Math.min(a, b));
}

export function maxCents(a: Cents, b: Cents): Cents {
    return toCents(Math.max(a, b));
}

/**
 * Redondeo AEAT — "redondeo a céntimo por magnitud declarada, medio hacia arriba" (T0.4).
 * Recibe céntimos con fracción (resultado intermedio de aplicar un % antes de redondear) y
 * devuelve céntimos enteros. 0,5 siempre redondea hacia el entero superior (Math.floor(x+0.5)),
 * incluida la zona negativa (hacia +∞, no simétrico) — ningún golden de F0 ejercita el caso
 * negativo con fracción de céntimo exacta; si una fase posterior lo contradice, corregir aquí
 * citando el golden (regla dura §0.4: golden es la verdad).
 */
export function roundHalfUp(centsWithFraction: number): Cents {
    return toCents(Math.floor(centsWithFraction + 0.5));
}
